import { useEffect, useMemo, useRef, useState } from "react";
import useFirestore from "../../../../hooks/useFirestore";
import { fetchListingById } from "../../../../utils/listings/fetchListings";
import type { REST_schema } from "../../../../@types/sharedSchema";
import { listingImageChoices, listingParamValues, listingSourceOf } from "./templateListing";
import type { PickedListing } from "./templateListing";

type TemplateView = REST_schema.WhatsappTemplateView;

export type TemplateListingState = {
  listing: PickedListing | null;
  /** True while the picked listing's stored document loads (its description and pictures). */
  loading: boolean;
  pick: (listing: PickedListing | null) => void;
  /**
   * The header this send carries. For an IMAGE template it is a picture of the listing or one the
   * realtor uploaded — never the template's approval sample — and undefined until one is chosen.
   */
  headerImageUrl: string | undefined;
  setHeaderImageUrl: (url: string | undefined) => void;
  /** An uploaded file becomes a choice of its own, kept when a listing is picked or changed. */
  addUpload: (url: string) => void;
  uploads: string[];
  /** The picked listing's pictures, as `listingImageChoices` offers them. */
  imageChoices: string[];
};

/**
 * The listing a template send is about, and the picture it goes out with — ONE piece of state for
 * every dialog that fills a template (the CRM's send, the campaign wizard, the single send).
 *
 * Picking a listing hands its values to `onListingValues` (the dialog pre-fills its fields with
 * them; the realtor may edit), and puts the listing's first picture in the header — unless the
 * realtor uploaded one, which is a choice a listing does not overrule. Changing the template starts
 * over, because the next one may read other fields.
 */
export default function useTemplateListing(
  template: TemplateView | undefined,
  onListingValues: (values: Record<string, string>) => void
): TemplateListingState {
  const { userSettings } = useFirestore();
  const [listing, setListing] = useState<PickedListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | undefined>(undefined);
  const [uploads, setUploads] = useState<string[]>([]);
  // Read by the listing's async load, which must see an upload made while it was loading.
  const uploadsRef = useRef<string[]>([]);
  const request = useRef(0);
  // The pictures are offered clean only to a realtor who accepted the watermark terms — the one gate.
  const clean = Boolean(userSettings?.watermarkTermsAcceptedAt);

  useEffect(() => {
    request.current += 1;
    setListing(null);
    setLoading(false);
    setHeaderImageUrl(undefined);
    setUploads([]);
    uploadsRef.current = [];
  }, [template?.id]);

  const imageChoices = useMemo(() => (listing ? listingImageChoices(listing, { clean }) : []), [listing, clean]);

  /** The header stays on an upload; anything else follows the listing (undefined: there is none to show). */
  const followListing = (next: string | undefined) =>
    setHeaderImageUrl((current) => (current && uploadsRef.current.includes(current) ? current : next));

  const pick = (picked: PickedListing | null) => {
    const seq = ++request.current;
    if (!picked) {
      setListing(null);
      followListing(undefined);
      return;
    }
    setListing(picked);
    setLoading(true);
    // The search hit lacks the description and the watermark-free pictures; the stored document has
    // them. Both, then — and the hit alone if the read fails. (The link is the server's: each
    // recipient's own minisite page.)
    fetchListingById(picked.id)
      .catch(() => null)
      .then((stored) => {
        if (seq !== request.current || !template) return;
        const full: PickedListing = { ...picked, ...(stored ?? {}), id: picked.id };
        setListing(full);
        onListingValues(listingParamValues(template, listingSourceOf(full)));
        if (template.headerFormat === "IMAGE") {
          followListing(listingImageChoices(full, { clean })[0]);
        }
      })
      .finally(() => {
        if (seq === request.current) setLoading(false);
      });
  };

  const addUpload = (url: string) => {
    if (!uploadsRef.current.includes(url)) uploadsRef.current = [...uploadsRef.current, url];
    setUploads(uploadsRef.current);
    setHeaderImageUrl(url);
  };

  return { listing, loading, pick, headerImageUrl, setHeaderImageUrl, addUpload, uploads, imageChoices };
}
